import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { StepContainer, StepContent, StepDescription, StepHeading, StepNavigator } from '../../components';
import { useIsMobile } from '@uplift/hooks';
import { Button } from '@uplift/ui-v2';
import { Fragment } from 'react';
import { environment } from '../../../../../environments/environment';

export type WelcomeProps = {
  onNext: () => void;
};

export const Welcome: React.VFC<WelcomeProps> = props => {
  const isMobile = useIsMobile();
  const { classes } = useWelcomeStyles();

  const desktopPng = 'https://scdn.joinuplift.co/public/application-assets/onboarding_image_desktop.webp';
  const mobilePng = 'https://scdn.joinuplift.co/public/application-assets/onboarding_image_mobile.webp';
  const imageUrl = isMobile ? mobilePng : desktopPng;

  const actions = (
    <Fragment>
      <Button variant="contained" color="secondary" className={classes.getStarted} onClick={props.onNext}>Get Started</Button>
      <Typography className={classes.existingAccount}>
        Already have an account?&nbsp;
        <a href={environment.authUrl} className={classes.link}>Sign in</a>
      </Typography>
    </Fragment>
  );

  return (
    <StepContainer>
      <div className={classes.container}>
        <div className={classes.imageContainer}>
          <img
            className={classes.image}
            src={imageUrl}
            alt="Welcome to UpLift!"
          />
        </div>
        <StepContent className={classes.content}>
          <Box mt={{ md: 'auto' }} mb={{ xs: 3, md: 0 }}>
            <StepHeading variant="h1">
              Tell us about&nbsp;
              <Typography component="span" color="secondary" variant="inherit">you</Typography>
            </StepHeading>
            <StepDescription variant={isMobile ? 'body1' : 'h4'}>We’ll match you with someone who understands you and accepts your insurance.</StepDescription>
            {!isMobile && actions}
          </Box>
          <div className={classes.preventionContainer}>
            <Typography variant="body2">If you are considering suicide or if you or any other person may be in danger, please call or text 988 (24-hour suicide and crisis lifeline) or call 911.</Typography>
          </div>
        </StepContent>
      </div>
      {isMobile && <StepNavigator>{actions}</StepNavigator>}
    </StepContainer>
  );
};

const useWelcomeStyles = makeStyles({ name: 'Welcome' })(theme => ({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.down('md')]: {
      background: theme.palette.beige.main,
    },

    [theme.breakpoints.up('md')]: {
      display: 'grid',
      gridTemplateColumns: '550px 1fr',
    }
  },
  imageContainer: {
    display: 'flex',
    height: '370px',
    [theme.breakpoints.up('md')]: {
      height: '100%',
    }
  },
  image: {
    objectFit: 'cover',
    width: '100%',
    height: '100%',
    objectPosition: 'bottom'
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    maxWidth: `calc(497px + ${theme.spacing(4)})`,
    paddingBottom: 0,
  },
  getStarted: {
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(3),
      width: 'fit-content'
    }
  },
  existingAccount: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '36px',
    padding: theme.spacing(0, 2),

    [theme.breakpoints.up('md')]: {
      display: 'block',
      marginTop: theme.spacing(2),
      padding: 0,
      height: 'unset',
    }
  },
  link: {
    color: 'inherit'
  },
  preventionContainer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    marginTop: 'auto',
    marginLeft: -theme.spacing(2),
    marginRight: -theme.spacing(2),
  },
}));
